/** Quick date preset buttons for CDRs page. */
import React from "react";
import { Box, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import { CalendarToday as CalendarIcon } from "@mui/icons-material";
import {
  startOfDay,
  endOfDay,
  subDays,
  subMonths,
  startOfMonth,
  endOfMonth,
} from "date-fns";

/** Date preset options. */
const datePresets = [
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 Days", value: "last_7_days" },
  { label: "Last 30 Days", value: "last_30_days" },
  { label: "This Month", value: "this_month" },
  { label: "Last Month", value: "last_month" },
];

interface QuickDatePresetsProps {
  value: string;
  onChange: (period: string, startDate: Date, endDate: Date) => void;
}

/** Get date range for a preset period. */
function getDateRangeForPeriod(period: string): { start: Date; end: Date } {
  const now = new Date();

  switch (period) {
    case "today":
      return {
        start: startOfDay(now),
        end: endOfDay(now),
      };
    case "yesterday":
      return {
        start: startOfDay(subDays(now, 1)),
        end: endOfDay(subDays(now, 1)),
      };
    case "last_7_days":
      return {
        start: startOfDay(subDays(now, 7)),
        end: endOfDay(now),
      };
    case "last_30_days":
      return {
        start: startOfDay(subDays(now, 30)),
        end: endOfDay(now),
      };
    case "this_month":
      return {
        start: startOfMonth(now),
        end: endOfDay(now),
      };
    case "last_month":
      return {
        start: startOfMonth(subMonths(now, 1)),
        end: endOfMonth(subMonths(now, 1)),
      };
    default:
      return {
        start: startOfDay(now),
        end: endOfDay(now),
      };
  }
}

/** Quick date preset button group component. */
export default function QuickDatePresets({
  value,
  onChange,
}: QuickDatePresetsProps) {
  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newPeriod: string | null
  ) => {
    if (newPeriod !== null) {
      const { start, end } = getDateRangeForPeriod(newPeriod);
      onChange(newPeriod, start, end);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        mb: 2,
      }}
    >
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={handleChange}
        aria-label="Date range preset"
        sx={{
          flexWrap: "wrap",
          justifyContent: "center",
          "& .MuiToggleButtonGroup-grouped": {
            "&:not(:first-of-type)": {
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            },
            "&:not(:last-of-type)": {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            },
          },
        }}
      >
        {datePresets.map((preset) => (
          <Tooltip key={preset.value} title={preset.label}>
            <ToggleButton
              value={preset.value}
              aria-label={preset.label}
              sx={{
                px: 2,
                py: 1,
                textTransform: "none",
                fontWeight: value === preset.value ? "bold" : "normal",
              }}
            >
              <CalendarIcon sx={{ mr: 0.5, fontSize: 18 }} />
              {preset.label}
            </ToggleButton>
          </Tooltip>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
}
